import React, { useCallback, useMemo } from 'react'
import styled from '@emotion/styled'
import { css } from '@emotion/core'
import Color from 'color'
import { useIntl } from 'react-intl'
import { compact } from 'lodash'

// components
import { ParagraphSmallCaps, ParagraphLegend } from 'components/Paragraphs'

// constants
const shapeSize = 12
const shapeBorderWidth = 1

const Container = styled.div(({ height }) => css`
  height: ${height}px;
`)

const Infos = styled.div`
  position: relative;
`
const IndicatorLabel = styled(ParagraphSmallCaps)`
  padding-bottom: 3px;
`

const InfosContainer = styled.div`
  text-align: right;
  position: absolute;
  right: 0;
  bottom: 3px;
`

const NodeInfosStyled = styled(ParagraphSmallCaps)(({ theme, isPrimary }) => css`
  color: ${isPrimary ? theme.tonic : theme.white};
  opacity: ${isPrimary ? 1 : 0.6};
  display: flex;
  justify-content: flex-end;
`)
const NodeLabel = styled.span`
  display: inline-block;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 180px; 
  white-space: nowrap;
  margin-right: 5px;
  opacity: 0.6;
`

const NodeInfos = ({ node, indicator, index, isPrimary }) => {
  const grade = index === 0 &&
    node.data.nature === 'subject' &&
    ` (${node.parent.data.label.replace('Grade ', '')})`
  return (
    <NodeInfosStyled isPrimary={isPrimary}>
      <NodeLabel>
        {index === 0 && node.data.label}
        {grade}
      </NodeLabel>
      <span>
        {`${node.textValue}`}
        {` ${indicator.unit(node.value)}`}
      </span>
    </NodeInfosStyled>
  )
}

const LegendContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 1px;
`

const Legend = styled(ParagraphLegend)(({ theme }) => css`
  color: ${theme.white};
  opacity: 0.5;
`)

const LegendMin = styled(Legend)`
  text-align: right;
`

const Axis = styled.div(({ theme, width }) => css`
  position: relative;
  height: ${shapeSize + shapeBorderWidth}px;
  width: ${width}px;
  :before {
    content: '';
    height: 1px;
    width: 100%;
    background: ${theme.separator};
    position: absolute;
    top: 50%;
    left: 0;
    transform: translateY(-50%);
  }
`)

const Shape = styled.div(({
  x,
  isSpecial,
  isPrimary,
  theme,
  isConcept,
  isCurrentConcept
}) => {
  // concept colors
  const conceptBgColor = Color(theme.purpleMedium).alpha(0.6).toString()
  const conceptBorderColor = theme.separator
  const conceptOpacity = isCurrentConcept ? 1 : 0.6

  const bgColor = isSpecial
    ? (isPrimary ? theme.tonic : theme.blueDark)
    : isConcept ? conceptBgColor : theme.purpleMedium
  const borderColor = isSpecial
    ? (isPrimary ? theme.tonic : theme.white)
    : isConcept ? conceptBorderColor : theme.separator
  const opacity = isSpecial ? 1 : isConcept ? conceptOpacity : 0.3

  return css`
    position: absolute;
    cursor: pointer;
    top: 50%;
    transform: translate(-50%, -50%) rotate(45deg);
    left: ${x}px;
    width: ${shapeSize}px;
    height: ${shapeSize}px;
    background: ${bgColor};
    opacity: ${opacity};
    border: ${shapeBorderWidth}px solid ${borderColor};
    z-index: ${isSpecial && isPrimary ? 10 : isSpecial ? 11 : 0};
    ${!isSpecial && (isConcept && !isCurrentConcept)
      ? css`
          :after {
            content: '';
            position: absolute;
            left: 2px;
            top: 2px;
            background: ${theme.purpleLight};
            width: ${shapeSize - 6}px;
            height: ${shapeSize - 6}px;
          }`
      : ''
    }
`
})

const Losange = ({
  nodeId,
  x,
  setSelection,
  selection,
  isSpecial,
  isPrimary,
  isConcept,
  isCurrentConcept
}) => {
  const isSelected = useMemo(() => {
    return selection.filter((d) => d.isFixed).map((d) => d.id).includes(nodeId)
  }, [selection, nodeId])
  const isHighlighted = useMemo(() => {
    return selection.filter((d) => !d.isFixed).map((d) => d.id).includes(nodeId)
  }, [selection, nodeId])

  const onMouseEnter = useCallback(() => {
    if (isSelected || selection.filter((d) => d.isFixed).length === 2) return

    const newItem = !isHighlighted && {
      id: nodeId,
      isFixed: false
    }

    const newSelection = compact([
      ...selection.filter((d) => d.id !== nodeId && d.isFixed),
      newItem
    ])

    setSelection(newSelection)
  }, [setSelection, selection, nodeId, isHighlighted, isSelected])

  const onMouseLeave = useCallback(() => {
    const newSelection = selection.filter((d) => d.isFixed)
    setSelection(newSelection)
  }, [setSelection, selection])

  const onClick = useCallback((e) => {
    e.stopPropagation()
    const newItem = !isSelected && {
      id: nodeId,
      isFixed: true
    }

    setSelection(compact([
      selection.find((d) => d.id !== nodeId && d.isFixed),
      newItem
    ]))
  }, [selection, nodeId, isSelected, setSelection])

  return (
    <Shape
      x={x}
      isSpecial={isSpecial}
      isPrimary={isPrimary}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      isConcept={isConcept}
      isCurrentConcept={isCurrentConcept}
    />
  )
}

const NodesAverageBar = styled.div(({ theme, x }) => css`
  position: absolute;
  left: ${x || 0}px;
  top: 50%;
  transform: translate(-50%, -50%);
  background-color: ${theme.blueSky};
  width: 3px;
  height: 20px;
`)

const Row = ({
  indicator,
  index,
  nodes,
  height,
  width,
  scale,
  setSelection,
  selection,
  populationId,
  averagePosition,
  isConcept,
  currentNode,
  isPrimaryParent,
  isSecondaryParent
}) => {
  const { messages } = useIntl()
  const primaryNode = useMemo(() => {
    return selection.length > 0 && nodes.find(node => selection[0].id === node.data.id)
  }, [nodes, selection])

  const secondaryNode = useMemo(() => {
    return selection.length > 1 && nodes.find(node => selection[1].id === node.data.id)
  }, [nodes, selection])

  const HighlightedDatum = useMemo(() => {
    return (
      <InfosContainer>
        {
          secondaryNode && !isSecondaryParent && (
            <NodeInfos
              node={secondaryNode}
              indicator={indicator}
              index={index}
            />
          )
        }
        {
          primaryNode && !isPrimaryParent && (
            <NodeInfos
              node={primaryNode}
              indicator={indicator}
              index={index}
              isPrimary
            />
          )
        }
      </InfosContainer>
    )
  }, [indicator, index, isPrimaryParent, isSecondaryParent, primaryNode, secondaryNode])

  const indicatorLabel = indicator.label

  return (
    <Container height={height}>
      <Infos>
        <div title={messages[indicator.tooltip]}>
          <IndicatorLabel>
            {indicatorLabel}
          </IndicatorLabel>
        </div>
        {HighlightedDatum}
      </Infos>
      <Axis width={width}>
        {
          nodes.map(node => {
            const isCurrentConcept = isConcept && currentNode.data.id === node.data.id
            return (
              <Losange
                key={node.data.id}
                nodeId={node.data.id}
                setSelection={setSelection}
                selection={selection}
                isConcept={isConcept}
                isCurrentConcept={isCurrentConcept}
                isSpecial={node.isPrimary || node.isSecondary}
                isPrimary={node.isPrimary}
                x={node.x}
              />
            )
          })
        }
        {
          averagePosition && (
            <NodesAverageBar x={averagePosition} />
          )
        }
      </Axis>
      <LegendContainer>
        <LegendMin>
          {scale.min.text} {scale.min.unit}
        </LegendMin>
        <Legend>
          {scale.max.text} {scale.max.unit}
        </Legend>
      </LegendContainer>
    </Container>
  )
}

export default Row
